import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SERVER_EVENTS } from '@superchat/shared/events';
import {
  hasPermission,
  ALERT_MODES,
  DEFAULT_ALERT_MODE,
  RINGING_ALERT_MODES,
} from '@superchat/shared/constants';
import { api } from './api.js';
import { useAuth } from './auth.jsx';
import { createAgentSocket } from './socket.js';
import {
  playIncoming,
  playEmergency,
  playFor,
  primeAudio,
  startRinging,
  stopRinging,
} from './sound.js';
import { notify } from './notify.js';
import { appColor } from './format.js';
import { createNotificationFeed } from './notification-feed.js';

const WorkspaceContext = createContext(null);

/** Conversations fetched per "load more", merged across every app in scope. */
const CONVERSATION_PAGE_SIZE = 50;

const byLastMessage = (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt);

const MUTE_KEY = 'superchat:muted';
const APP_KEY = 'superchat:activeApp';
const ALERT_KEY = 'superchat:alertMode';

/**
 * The "every app at once" scope, stored in the same slot as an app id.
 *
 * An agency watching six tenants spends its day switching between them to find
 * out whether anything happened; this is the scope where it does not have to.
 * Chat, Tickets and Docs then read from every app the agent can reach, and each
 * row carries the app it belongs to — because the moment the lists are merged,
 * "which tenant is this" becomes the first thing you need to know.
 *
 * Deliberately not a scope for Analytics, Integration or Team: those three are
 * per-tenant *configuration*, and there is no such thing as the install snippet
 * of six apps. They ask for one app instead.
 */
export const ALL_APPS = '*';

/**
 * The stored alert mode, tolerating what earlier builds wrote.
 *
 * This key used to hold a boolean: "alert mode armed" or not. An agent who had
 * armed it walked away expecting an alarm, so `true` becomes the mode that
 * still rings rather than the quiet default.
 */
function readAlertMode() {
  const stored = localStorage.getItem(ALERT_KEY);
  if (stored === 'true') return 'continuous';
  if (stored === 'false' || stored === null) return DEFAULT_ALERT_MODE;
  return ALERT_MODES.includes(stored) ? stored : DEFAULT_ALERT_MODE;
}

/**
 * Tenant scope for the whole console: which app is selected, the live socket
 * for that app, and the conversation list every view reads from.
 *
 * Switching apps tears down the socket and opens a new one — the server joins
 * `app:{appId}` only after proving membership, so scope is never a client-side
 * filter over a shared stream.
 */
export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  // Owner + members of the focused app: who a conversation or ticket may be
  // handed to. Loaded here rather than per view, because the chat rail and the
  // tickets table need the same list and asked for it twice.
  // Keyed by appId, because in the all-apps scope a ticket on tenant A may not
  // be assignable to anyone on tenant B — the valid assignees are whoever can
  // reach the app the item itself belongs to.
  const [membersByApp, setMembersByApp] = useState({});
  /**
   * Either an appId or ALL_APPS. One slot rather than an id plus a flag, so
   * there is exactly one thing to persist and exactly one thing to restore.
   */
  const [scopeId, setScopeId] = useState(() => localStorage.getItem(APP_KEY));
  const [loadingApps, setLoadingApps] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [connection, setConnection] = useState('connecting');
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === 'true');
  // Persisted so it survives a reload — an agent who armed the alarm and
  // walked away should still find it armed.
  const [alertMode, setAlertModeState] = useState(readAlertMode);
  const [toast, setToast] = useState(null);
  // Keyed by visitorId. Seeded by one presence fetch, then kept current by
  // `presence:changed` events — no polling, so the list cannot flicker between
  // a stale snapshot and a fresh one.
  const [presence, setPresence] = useState({ visitors: {}, agents: [], clusterWide: true });
  // Unread on apps the agent is *not* looking at, keyed by appId. The focused
  // app's count comes from the conversations themselves; this covers the rest.
  const [appUnread, setAppUnread] = useState({});
  /**
   * Things that are ringing but have nothing unread behind them.
   *
   * Continuous mode rings on a visitor arriving and on a visitor opening the
   * chat, neither of which produces a message — so neither can be expressed as
   * derived unread state the way an incoming message is. They are tracked
   * explicitly instead, keyed `kind:visitorId`, and cleared on the same
   * principle as everything else here: the agent looking at the thread is the
   * acknowledgement, and a visitor who has left is no longer waiting.
   */
  const [pendingAlerts, setPendingAlerts] = useState({});
  /**
   * Which colleagues have which conversation open, keyed by conversationId.
   *
   * Held here rather than in ChatView because the server announces it into the
   * app room — an agent hears that a thread is occupied before they open it,
   * which is the only useful moment to hear it. Entries are replaced wholesale:
   * the server sends the full list each time, so there is no merge to get wrong
   * and no way for a stale viewer to survive a missed departure.
   */
  const [viewers, setViewers] = useState({});

  const socketRef = useRef(null);
  const listeners = useRef(new Set());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const alertModeRef = useRef(alertMode);
  alertModeRef.current = alertMode;
  // Socket handlers ask "is this mine?" without re-subscribing when the
  // profile is refreshed.
  const userIdRef = useRef(user?.id ?? null);
  userIdRef.current = user?.id ?? null;
  // Read inside socket handlers without making them a dependency — depending
  // on `conversations` would tear down and rebuild the socket on every message.
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  // Sound and unread accounting depend on what the agent is looking at.
  const openConversationRef = useRef(null);

  /* ------------------------------------------------------------------ apps */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.apps.list();
        if (cancelled) return;
        setApps(list);
        setScopeId((current) => {
          // The all-apps scope survives a reload as long as there is anything
          // to combine; on a bare account it falls back like any stale id.
          if (current === ALL_APPS) return list.length > 0 ? ALL_APPS : null;
          return current && list.some((a) => a.id === current) ? current : (list[0]?.id ?? null);
        });
      } catch {
        if (!cancelled) setApps([]);
      } finally {
        if (!cancelled) setLoadingApps(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (scopeId) localStorage.setItem(APP_KEY, scopeId);
  }, [scopeId]);

  const allApps = scopeId === ALL_APPS;
  /**
   * Null in the all-apps scope, and every per-tenant view already treats "no
   * app" as "ask for one" — which is exactly the right behaviour there. Views
   * that work across tenants read `scopedApps` instead, and act on the appId
   * carried by the row in hand rather than on a single ambient one.
   */
  const activeAppId = allApps ? null : scopeId;

  const activeApp = useMemo(
    () => apps.find((a) => a.id === activeAppId) ?? null,
    [apps, activeAppId]
  );

  const appIndex = useMemo(
    () => Object.fromEntries(apps.map((a, i) => [a.id, { ...a, color: appColor(a.id, i), index: i }])),
    [apps]
  );

  /** Every app the current scope covers: all of them, or the one in focus. */
  const scopedApps = useMemo(
    () => (allApps ? apps : activeApp ? [activeApp] : []),
    [allApps, apps, activeApp]
  );

  // A stable identity for the *set* of apps in scope, so the loaders below
  // re-run when the scope genuinely changes rather than on every new array.
  const scopeKey = useMemo(() => scopedApps.map((a) => a.id).sort().join(','), [scopedApps]);

  /**
   * The socket still needs one app to prove membership with, but the server
   * joins every app room the token can reach on a single connection — so in the
   * all-apps scope any reachable app will do as the anchor.
   */
  const socketAppId = allApps ? (apps[0]?.id ?? null) : activeAppId;

  // Read inside socket handlers, which must not re-subscribe when either
  // changes — the handler is a socket dependency and rebuilding it drops the
  // connection.
  const activeAppIdRef = useRef(activeAppId);
  activeAppIdRef.current = activeAppId;
  const appIndexRef = useRef(appIndex);
  appIndexRef.current = appIndex;
  // In the all-apps scope there is no "somewhere else" for an event to be
  // filed under, so the other-app routing below is skipped entirely.
  const allAppsRef = useRef(allApps);
  allAppsRef.current = allApps;

  // Switching to an app clears its badge; the conversation list it loads is
  // then the source of truth for what is unread there. The all-apps scope
  // clears every badge for the same reason — all of it is now on screen.
  useEffect(() => {
    if (allApps) {
      setAppUnread((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }
    if (!activeAppId) return;
    setAppUnread((current) => {
      if (!current[activeAppId]) return current;
      const next = { ...current };
      delete next[activeAppId];
      return next;
    });
  }, [activeAppId, allApps]);

  /* --------------------------------------------------------- conversations */

  /**
   * The inbox for the current scope: one app, or every app merged.
   *
   * Fanned out client-side rather than through a cross-tenant endpoint,
   * because there is no such endpoint by design — every list on the API is
   * scoped to one app and authorised as one app. One app that 403s or falls
   * over drops out of the merge instead of emptying the whole inbox.
   */
  /**
   * Paged with the same merge the notification bell uses: every app keeps its
   * own cursor, and a row is only handed out once no app could still be hiding
   * a newer one — so "load more" in the all-apps scope stays in true order.
   */
  const conversationFeed = useRef(null);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);

  const loadConversations = useCallback(async (appIds) => {
    // No tenant selected is a settled state, not a pending one — leaving the
    // flag set spins every view forever.
    if (appIds.length === 0) {
      conversationFeed.current = null;
      setConversations([]);
      setHasMoreConversations(false);
      setLoadingConversations(false);
      return;
    }
    const feed = createNotificationFeed({
      appIds,
      pageSize: CONVERSATION_PAGE_SIZE,
      timeOf: (c) => c.lastMessageAt,
      fetchPage: (appId, cursor) =>
        api.conversations.list(appId, {
          limit: CONVERSATION_PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
        }),
    });
    conversationFeed.current = feed;
    setLoadingConversations(true);
    setLoadingMoreConversations(false);
    try {
      const page = await feed.next();
      // A newer scope has replaced this load while it was in flight.
      if (conversationFeed.current !== feed) return;
      setConversations([...page.items].sort(byLastMessage));
      setHasMoreConversations(page.hasMore);
    } catch {
      if (conversationFeed.current !== feed) return;
      setConversations([]);
      setHasMoreConversations(false);
    } finally {
      if (conversationFeed.current === feed) setLoadingConversations(false);
    }
  }, []);

  const loadMoreConversations = useCallback(async () => {
    const feed = conversationFeed.current;
    if (!feed || loadingMoreConversations) return;
    setLoadingMoreConversations(true);
    try {
      const page = await feed.next();
      if (conversationFeed.current !== feed) return;
      setConversations((current) => {
        // A socket update may already have brought an older thread in, and
        // that copy is fresher than the one the page was fetched with.
        const known = new Set(current.map((c) => c.id));
        const added = page.items.filter((c) => !known.has(c.id));
        return added.length ? [...current, ...added].sort(byLastMessage) : current;
      });
      setHasMoreConversations(page.hasMore);
    } catch {
      /* the button stays, so the agent can simply try again */
    } finally {
      if (conversationFeed.current === feed) setLoadingMoreConversations(false);
    }
  }, [loadingMoreConversations]);

  const scopedAppIds = useMemo(() => (scopeKey ? scopeKey.split(',') : []), [scopeKey]);

  useEffect(() => {
    setConversations([]);
    loadConversations(scopedAppIds);
  }, [scopedAppIds, loadConversations]);

  /* -------------------------------------------------------------- members */

  /**
   * The roster is the set of valid assignees, so it is scoped to the app in
   * focus and reloaded with it. `GET /members` answers with
   * `{ members, invites, canManage }` — only the first is a list, and each
   * entry is keyed by `userId`, not `id`.
   */
  useEffect(() => {
    if (scopedAppIds.length === 0) {
      setMembersByApp({});
      return undefined;
    }
    let live = true;
    Promise.all(
      scopedAppIds.map((appId) =>
        api.members
          .list(appId)
          .then((roster) => [appId, Array.isArray(roster?.members) ? roster.members : []])
          .catch(() => [appId, []])
      )
    ).then((entries) => {
      if (live) setMembersByApp(Object.fromEntries(entries));
    });
    return () => {
      live = false;
    };
  }, [scopedAppIds]);

  /**
   * Every colleague in scope, deduplicated — the roster used for *naming* the
   * person on a row. Choosing an assignee is a different question and must be
   * asked per app, which is what `membersByApp` is for.
   */
  const members = useMemo(() => {
    const seen = new Map();
    for (const roster of Object.values(membersByApp)) {
      for (const member of roster) if (!seen.has(member.userId)) seen.set(member.userId, member);
    }
    return [...seen.values()];
  }, [membersByApp]);

  /* ------------------------------------------------------------- presence */

  const loadPresence = useCallback(async (appIds) => {
    if (appIds.length === 0) {
      setPresence({ visitors: {}, agents: [], clusterWide: true });
      return;
    }
    try {
      const snapshots = await Promise.all(
        appIds.map((appId) =>
          api.presence
            .list(appId)
            .catch(() => ({ visitors: [], agents: [], clusterWide: true }))
        )
      );
      // Keyed by visitorId across apps, the same key the socket's
      // `presence:changed` uses — visitor ids are minted per widget install, so
      // two tenants sharing one is not a case that arises.
      setPresence({
        visitors: Object.fromEntries(
          snapshots.flatMap((s) => s.visitors.map((v) => [v.visitorId, v]))
        ),
        agents: snapshots.flatMap((s) => s.agents),
        clusterWide: snapshots.every((s) => s.clusterWide),
      });
    } catch {
      setPresence({ visitors: {}, agents: [], clusterWide: true });
    }
  }, []);

  useEffect(() => {
    loadPresence(scopedAppIds);
  }, [scopedAppIds, loadPresence]);

  /**
   * Reconcile presence whenever we may have missed a transition.
   *
   * `presence:changed` is fire-and-forget: the server emits "went offline" once,
   * six seconds after the last socket drops, and Socket.IO does not replay it.
   * A dashboard that was disconnected at that moment — a token refresh, a
   * closed laptop, a deploy — never hears it, and the visitor stays "online"
   * forever. That was the hour-old ghost.
   *
   * A reconnect and a tab waking up are exactly the moments the local map is
   * most likely to be wrong, so both re-fetch the authoritative snapshot. This
   * is not polling: a dashboard sitting connected and visible never calls it.
   */
  useEffect(() => {
    if (scopedAppIds.length === 0) return undefined;

    if (connection === 'connected') loadPresence(scopedAppIds);

    const onVisible = () => {
      if (document.visibilityState === 'visible') loadPresence(scopedAppIds);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [scopedAppIds, connection, loadPresence]);

  /* ----------------------------------------------------------------- sound */

  /**
   * Whether a one-shot tone may sound right now.
   *
   * Two switches rather than one, deliberately: `silent` is a considered
   * choice about the whole console and nothing overrides it, while the mute
   * button beside it is a quick "not now" that an emergency may talk over.
   */
  const canChime = useCallback((isEmergency = false) => {
    if (alertModeRef.current === 'silent') return false;
    return isEmergency || !mutedRef.current;
  }, []);

  const chime = useCallback(
    (isEmergency = false) => {
      if (!canChime(isEmergency)) return;
      if (isEmergency) playEmergency();
      else playIncoming();
    },
    [canChime]
  );

  /** A one-shot tone for a named trigger, so arrivals can sound unlike replies. */
  const chimeFor = useCallback(
    (trigger) => {
      if (!canChime(false)) return;
      playFor(trigger);
    },
    [canChime]
  );

  /**
   * Raises a ringing alert that has no unread message behind it.
   *
   * Only in `continuous` mode. `critical` deliberately stays narrowed to
   * threads with something unread on them — its whole definition is "somebody
   * is sitting there watching for a reply", and an arrival is not yet that.
   */
  const raiseAlert = useCallback((kind, { visitorId, conversationId }) => {
    if (alertModeRef.current !== 'continuous') return;
    setPendingAlerts((current) => ({
      ...current,
      [`${kind}:${visitorId}`]: { kind, visitorId, conversationId },
    }));
  }, []);

  /** Drops every pending alert matching a predicate — an acknowledgement. */
  const clearAlerts = useCallback((match) => {
    setPendingAlerts((current) => {
      const next = Object.fromEntries(Object.entries(current).filter(([, a]) => !match(a)));
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, []);

  /* ---------------------------------------------------------------- socket */

  const emit = useCallback((event, payload) => {
    for (const listener of listeners.current) listener(event, payload);
  }, []);

  /**
   * Something happened on an app the agent is not looking at.
   *
   * Slack's model: a badge on the app that has news, plus one notification that
   * names it and switches on click. Deliberately quieter than the focused app —
   * no sound for an ordinary message, because four merchants chiming at once
   * while you are mid-reply on a fifth is how people turn notifications off.
   * An emergency still overrides that.
   */
  const handleOtherApp = useCallback(
    (event, payload) => {
      const isMessage = event === SERVER_EVENTS.MESSAGE_NEW && payload.senderType === 'visitor';
      const isArrival = event === SERVER_EVENTS.PRESENCE_CHANGED && payload.online;
      if (!isMessage && !isArrival) return;
      // Blocked threads never interrupt, in any mode.
      if (payload.conversationBlocked || payload.blocked) return;

      const appName = appIndexRef.current[payload.appId]?.name ?? 'another app';

      if (isMessage) {
        setAppUnread((current) => ({
          ...current,
          [payload.appId]: (current[payload.appId] ?? 0) + 1,
        }));
      }

      if (payload.isEmergency) chime(true);
      else if (isMessage) chime();

      const title = payload.isEmergency
        ? `User alert · ${appName}`
        : isMessage
          ? `New message · ${appName}`
          : `Visitor online · ${appName}`;

      const body = isMessage
        ? payload.body.slice(0, 140)
        : `${payload.name || 'A visitor'} just opened the chat widget.`;

      // `force`, because the agent *is* looking at the dashboard — just at a
      // different app. The usual "tab is visible, the toast is enough" rule
      // would swallow exactly the case this exists for.
      notify({
        title,
        body,
        tag: `app:${payload.appId}`,
        appId: payload.appId,
        conversationId: payload.conversationId,
        force: true,
        requireInteraction: Boolean(payload.isEmergency),
      });

      setToast({
        kind: payload.isEmergency ? 'emergency' : 'other-app',
        title,
        body,
        appId: payload.appId,
        conversationId: payload.conversationId,
      });
      if (!payload.isEmergency) {
        setTimeout(() => setToast((t) => (t?.kind === 'other-app' ? null : t)), 8000);
      }
    },
    [chime]
  );

  const handleEvent = useCallback(
    (event, payload) => {
      /**
       * Assignment is addressed to a person, not to a tenant, so it is handled
       * before the per-app routing below: work handed to you on an app you are
       * not currently looking at is still yours, and quietly filing it under
       * "another app has news" would bury exactly the thing that needs saying.
       */
      if (event === SERVER_EVENTS.ASSIGNMENT_NEW) {
        if (payload.assigneeId === userIdRef.current) {
          chime();
          notify({
            title: `Assigned to you · ${payload.appName}`,
            body: `${payload.assignedByName} assigned you ${payload.title}`,
            tag: `assign:${payload.ticketId ?? payload.conversationId}`,
            appId: payload.appId,
            conversationId: payload.conversationId,
            ticketId: payload.ticketId,
            kind: payload.kind,
            // The agent may well be looking at the dashboard — at something
            // else. Being handed work is worth interrupting that.
            force: true,
          });
          setToast({
            kind: 'assignment',
            title: `${payload.assignedByName} assigned you a ${payload.kind}`,
            body: `${payload.title} · ${payload.appName}`,
            appId: payload.appId,
            conversationId: payload.conversationId,
            ticketId: payload.ticketId,
            // An assigned ticket lives on another view entirely; `what` is what
            // the toast's Open button routes on.
            what: payload.kind,
          });
          setTimeout(() => setToast((t) => (t?.kind === 'assignment' ? null : t)), 10000);
        }
        emit(event, payload);
        return;
      }

      /**
       * The socket carries every app this agent can reach, not just the one in
       * focus, so that an unselected app is not silent. Anything belonging
       * elsewhere is routed here and must never touch the state of the app on
       * screen — the conversation list, presence and unread counts below all
       * describe the focused app only.
       */
      if (
        !allAppsRef.current &&
        payload?.appId &&
        payload.appId !== activeAppIdRef.current
      ) {
        handleOtherApp(event, payload);
        return;
      }

      /**
       * Who else is in a thread. Handled before the sound and unread logic
       * below because it is pure bookkeeping — nothing about a colleague
       * opening a conversation should chime, badge or interrupt.
       */
      if (event === SERVER_EVENTS.CONVERSATION_VIEWERS) {
        setViewers((current) => ({ ...current, [payload.conversationId]: payload.viewers ?? [] }));
        emit(event, payload);
        return;
      }

      if (event === SERVER_EVENTS.CONVERSATION_UPDATED) {
        setConversations((current) => {
          const without = current.filter((c) => c.id !== payload.id);
          return [payload, ...without].sort(
            (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
          );
        });
      }

      /**
       * A visitor arrived or left. The list updates either way; muting a
       * visitor suppresses the interruption, not the fact that they are here.
       */
      if (event === SERVER_EVENTS.PRESENCE_CHANGED) {
        setPresence((current) => {
          const visitors = { ...current.visitors };
          if (payload.online) {
            visitors[payload.visitorId] = {
              ...visitors[payload.visitorId],
              ...payload,
              connectedAt: payload.at,
              sockets: 1,
            };
          } else {
            delete visitors[payload.visitorId];
          }
          return { ...current, visitors };
        });

        // Gone means no longer waiting. Both kinds of alert for this visitor go
        // with them, so an alarm cannot outlive the person who caused it.
        if (!payload.online) {
          clearAlerts((alert) => alert.visitorId === payload.visitorId);
        }

        if (payload.online && !payload.muted && !payload.blocked) {
          chimeFor('visitorOnline');
          raiseAlert('visitorOnline', {
            visitorId: payload.visitorId,
            conversationId: payload.conversationId,
          });
          const onApp = appIndexRef.current[payload.appId]?.name;
          notify({
            title: onApp ? `Visitor online · ${onApp}` : 'Visitor online',
            body: `${payload.name || 'A visitor'} just opened the chat widget.`,
            tag: `presence:${payload.visitorId}`,
            appId: payload.appId,
            conversationId: payload.conversationId,
          });
          setToast({
            kind: 'presence',
            title: 'Visitor online',
            body: `${payload.name || 'A visitor'} just opened the chat widget.`,
            appId: payload.appId,
            conversationId: payload.conversationId,
          });
          setTimeout(() => setToast((t) => (t?.kind === 'presence' ? null : t)), 8000);
        }
        emit(event, payload);
        return;
      }

      /**
       * The visitor opened the chat panel.
       *
       * A stronger signal than arriving on the page: they went looking for
       * support and are now watching an empty thread. Nothing is unread yet,
       * so this is the other case continuous mode has to be told about
       * explicitly rather than deriving.
       */
      if (event === SERVER_EVENTS.CHAT_OPENED) {
        if (!payload.muted && !payload.blocked) {
          chimeFor('chatOpened');
          raiseAlert('chatOpened', {
            visitorId: payload.visitorId,
            conversationId: payload.conversationId,
          });

          const onApp = appIndexRef.current[payload.appId]?.name;
          const body = `${payload.name || 'A visitor'} opened the chat and is waiting.`;
          notify({
            title: onApp ? `Chat opened · ${onApp}` : 'Chat opened',
            body,
            tag: `opened:${payload.visitorId}`,
            appId: payload.appId,
            conversationId: payload.conversationId,
          });
          setToast({
            kind: 'chat-opened',
            title: 'Chat opened',
            body,
            appId: payload.appId,
            conversationId: payload.conversationId,
          });
          setTimeout(() => setToast((t) => (t?.kind === 'chat-opened' ? null : t)), 8000);
        }
        emit(event, payload);
        return;
      }

      if (
        event === SERVER_EVENTS.MESSAGE_NEW &&
        payload.senderType === 'visitor' &&
        // The server's flag, or ours if an older server did not send one.
        !payload.conversationBlocked &&
        !conversationsRef.current.find((c) => c.id === payload.conversationId)?.blocked
      ) {
        const isOpen = openConversationRef.current === payload.conversationId;
        // Per-visitor mute, resolved from the conversation the message belongs
        // to. Emergencies ignore it, exactly as the server does.
        const conversation = conversationsRef.current.find(
          (c) => c.id === payload.conversationId
        );
        const visitorMuted = Boolean(conversation?.notifications?.agentMuted);
        const who = conversation?.visitor.name || 'Visitor';
        // Which tenant this is, in the banner itself. The agent may be on the
        // dashboard but looking elsewhere, and an OS notification is read in
        // isolation from whatever the app happens to have selected.
        const appName = appIndexRef.current[payload.appId]?.name;
        const from = appName ? `${who} · ${appName}` : who;

        if (payload.isEmergency) {
          chime(true);
          notify({
            title: `User alert — ${from}`,
            body: payload.body.slice(0, 140),
            tag: `conv:${payload.conversationId}`,
            appId: payload.appId,
            conversationId: payload.conversationId,
            force: true,
            requireInteraction: true,
          });
          setToast({
            kind: 'emergency',
            title: 'User alert',
            body: payload.body.slice(0, 140),
            appId: payload.appId,
            conversationId: payload.conversationId,
          });
        } else if (!visitorMuted) {
          if (!isOpen) chime();
          // Only when the tab is not being looked at — notify() enforces that.
          notify({
            title: from,
            body: payload.body.slice(0, 140),
            tag: `conv:${payload.conversationId}`,
            appId: payload.appId,
            conversationId: payload.conversationId,
          });

          /**
           * And a way in from inside the console.
           *
           * A message used to be announced in-app by a sound and nothing else:
           * an agent on Tickets or Analytics heard a chime with no clue which
           * thread it came from and no way to reach it except going hunting.
           * The desktop banner does not cover that — it deliberately stays
           * quiet while the tab is being looked at.
           *
           * Not raised for the thread already on screen, where the message has
           * just appeared in front of them and a toast would be noise.
           */
          if (!isOpen) {
            setToast({
              kind: 'message',
              title: from,
              body: payload.body.slice(0, 140) || 'Sent an attachment',
              appId: payload.appId,
              conversationId: payload.conversationId,
            });
            setTimeout(() => setToast((t) => (t?.kind === 'message' ? null : t)), 8000);
          }
        }
      }

      /**
       * A visitor testing their connection. Deliberately noisy — the whole
       * point is that the agent can confirm it arrived — but it writes nothing
       * and never touches unread counts.
       */
      if (event === SERVER_EVENTS.DIAGNOSTIC_PING) {
        // Only the visitor->agent direction concerns a dashboard; the reverse
        // is this same agent's own broadcast coming back around.
        if (payload.direction !== 'to-agents') return;

        chime(payload.isEmergency);

        setToast({
          kind: payload.isEmergency ? 'emergency' : 'ping',
          title: payload.isEmergency ? 'Test emergency from a visitor' : 'Test notification received',
          body: `${payload.fromName || 'A visitor'} sent a test from the widget — delivery is working.`,
          conversationId: payload.conversationId,
        });
        // An ordinary diagnostic clears itself; an emergency waits to be seen.
        if (!payload.isEmergency) {
          setTimeout(() => setToast((t) => (t?.kind === 'ping' ? null : t)), 8000);
        }
      }

      emit(event, payload);
    },
    [emit, chime, chimeFor, raiseAlert, clearAlerts, handleOtherApp]
  );

  useEffect(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    if (!socketAppId) {
      // Nothing to subscribe to; don't leave the UI claiming it is reconnecting.
      setConnection('idle');
      return undefined;
    }

    setConnection('connecting');
    socketRef.current = createAgentSocket({
      appId: socketAppId,
      onEvent: handleEvent,
      onStatus: setConnection,
    });

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [socketAppId, handleEvent]);

  /** Views subscribe to the raw socket stream; returns an unsubscribe. */
  const onSocketEvent = useCallback((listener) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);

  /* --------------------------------------------------------------- actions */

  /** Takes an appId or ALL_APPS — the switcher offers both from one list. */
  const selectApp = useCallback((appId) => setScopeId(appId), []);

  /** Creates a tenant and switches to it — the only path out of a fresh account. */
  const createApp = useCallback(async (payload) => {
    const app = await api.apps.create(payload);
    setApps((current) => [app, ...current]);
    setScopeId(app.id);
    return app;
  }, []);

  const setAlertMode = useCallback((mode) => {
    if (!ALERT_MODES.includes(mode)) return;
    // This click is the gesture that unlocks WebAudio; without it the alarm
    // would be armed but silent.
    primeAudio();
    localStorage.setItem(ALERT_KEY, mode);
    setAlertModeState(mode);
  }, []);

  const toggleMute = useCallback(() => {
    primeAudio(); // the click that flips this also unlocks WebAudio
    setMuted((current) => {
      localStorage.setItem(MUTE_KEY, String(!current));
      return !current;
    });
  }, []);

  const patchConversation = useCallback((conversation) => {
    setConversations((current) =>
      current.map((c) => (c.id === conversation.id ? conversation : c))
    );
  }, []);

  /**
   * Per-visitor notification mute, persisted on the conversation.
   *
   * Routed by the conversation's own appId rather than by whatever is in focus,
   * so it is correct in the all-apps scope where there is no app in focus.
   */
  const setConversationMuted = useCallback(
    async (conversationId, notificationsMuted) => {
      const appId =
        conversationsRef.current.find((c) => c.id === conversationId)?.appId ?? activeAppId;
      if (!appId) return null;
      const updated = await api.conversations.update(appId, conversationId, {
        notificationsMuted,
      });
      patchConversation(updated);
      return updated;
    },
    [activeAppId, patchConversation]
  );

  /**
   * Pins or unpins a thread for everyone on its app.
   *
   * Routed by the conversation's own appId rather than whatever is in focus, so
   * it works in the all-apps scope. Optimistic: the row jumps the moment it is
   * clicked and is reconciled from the server's answer, because a pin that
   * takes a round-trip to move feels broken in a list this dense. Colleagues
   * see it a moment later, off the CONVERSATION_UPDATED broadcast — the same
   * path that carries a status change.
   */
  const toggleConversationPin = useCallback(
    async (conversationId) => {
      const conversation = conversationsRef.current.find((c) => c.id === conversationId);
      const userId = userIdRef.current;
      if (!conversation) return null;

      const pinned = Boolean(conversation.pinned);
      patchConversation({
        ...conversation,
        pinned: !pinned,
        pinnedBy: pinned ? null : userId,
        pinnedAt: pinned ? null : new Date().toISOString(),
      });

      try {
        const updated = pinned
          ? await api.pins.remove(conversation.appId, conversationId)
          : await api.pins.add(conversation.appId, conversationId);
        patchConversation(updated);
        return updated;
      } catch {
        // Put it back where it was rather than leaving the list claiming
        // something the server never agreed to — a read-only member has no
        // pin to give, and finding out is what this catch is for.
        patchConversation(conversation);
        return null;
      }
    },
    [patchConversation]
  );

  const setOpenConversation = useCallback(
    (conversationId) => {
      openConversationRef.current = conversationId;
      // Looking at the thread is the acknowledgement. Unread-driven ringing
      // already stops here because opening marks the thread read; the arrival
      // and chat-opened alerts have no read state of their own, so they are
      // dismissed explicitly by the same act.
      if (conversationId) clearAlerts((alert) => alert.conversationId === conversationId);
    },
    [clearAlerts]
  );

  /* Sidebar counters, derived rather than tracked separately. */
  const unreadCount = useMemo(
    () => conversations.filter((c) => c.unread.agent > 0 && !c.blocked).length,
    [conversations]
  );

  /**
   * The ringing modes ring while something is still waiting, and stop the
   * moment it is read.
   *
   * Driven by derived unread state rather than by the arrival event, which is
   * what makes it self-correcting: opening the thread marks it read, the count
   * drops to zero, and the alarm stops without anyone telling it to. It also
   * survives a reload mid-alarm, and a muted visitor is excluded so muting
   * still means something.
   *
   * `critical` narrows "waiting" to threads whose visitor is still connected —
   * an unread message from someone who has already closed the tab is not the
   * thing worth waking a room over, whereas one from someone sitting there
   * watching for a reply is.
   */
  const ringing = useMemo(() => {
    if (!RINGING_ALERT_MODES.includes(alertMode)) {
      return { active: false, emergency: false, trigger: null };
    }

    const unread = conversations.filter(
      (c) => c.unread.agent > 0 && !c.notifications?.agentMuted && !c.blocked
    );
    const waiting =
      alertMode === 'critical'
        ? unread.filter((c) => Boolean(presence.visitors[c.visitor.visitorId]))
        : unread;

    const emergency = waiting.some((c) => c.hasEmergency);
    if (waiting.length > 0) {
      return { active: true, emergency, trigger: emergency ? 'emergency' : 'message' };
    }

    /**
     * Nothing unread, but somebody may still be waiting on the other two
     * triggers. Only `continuous` reaches this — `critical` is defined by
     * unread, so it has already returned above.
     *
     * A chat opened outranks an arrival when both are pending for different
     * visitors: it is the one where someone is looking at an empty thread.
     */
    const alerts = Object.values(pendingAlerts);
    if (alerts.length === 0) return { active: false, emergency: false, trigger: null };

    return {
      active: true,
      emergency: false,
      trigger: alerts.some((a) => a.kind === 'chatOpened') ? 'chatOpened' : 'visitorOnline',
    };
  }, [alertMode, conversations, presence.visitors, pendingAlerts]);

  useEffect(() => {
    if (ringing.active) startRinging({ emergency: ringing.emergency, trigger: ringing.trigger });
    else stopRinging();
  }, [ringing.active, ringing.emergency, ringing.trigger]);

  /* Leaving a ringing mode drops what was ringing, rather than parking it to
     go off again the next time the mode is turned on. */
  useEffect(() => {
    if (alertMode !== 'continuous') setPendingAlerts({});
  }, [alertMode]);

  // Never leave a ring running past unmount.
  useEffect(() => stopRinging, []);
  const emergencies = useMemo(
    () =>
      conversations.filter(
        (c) => c.hasEmergency && !c.blocked && !['resolved', 'closed'].includes(c.status)
      ),
    [conversations]
  );

  const value = useMemo(
    () => ({
      apps,
      appIndex,
      activeApp,
      activeAppId,
      /** True when Chat, Tickets and Docs are showing every app at once. */
      allApps,
      /** The apps the current scope covers — one, or all of them. */
      scopedApps,
      loadingApps,
      selectApp,
      createApp,
      /**
       * What the signed-in agent may do on the app currently in focus.
       *
       * Presentation only — the server enforces the same grant on every route,
       * so this exists to avoid offering buttons that would 403, not to secure
       * anything. It answers true while the app list is still loading, so the
       * UI does not flash a disabled state at the owner on every page load.
       */
      can: (scope, level) => {
        // Still loading: answer yes, so the nav does not show every link and
        // then take some away — that reads as a glitch.
        if (allApps && scopedApps.length > 0) {
          // In the all-apps scope a link is worth offering if *any* tenant
          // would honour it; the per-row check below decides each action.
          return scopedApps.some(
            (a) => !a.permissions || hasPermission(a.permissions, scope, level)
          );
        }
        return !activeApp?.permissions || hasPermission(activeApp.permissions, scope, level);
      },
      /**
       * The same question asked about one app by id — what a merged list needs,
       * where the row in hand decides which grant applies rather than the app
       * that happens to be selected.
       */
      canOn: (appId, scope, level) => {
        const permissions = appIndex[appId]?.permissions;
        return !permissions || hasPermission(permissions, scope, level);
      },
      isAppOwner: Boolean(activeApp?.isOwner),
      /** Unread on apps other than the one in focus, keyed by appId. */
      appUnread,
      otherAppsUnread: allApps
        ? 0
        : Object.values(appUnread).reduce((total, n) => total + n, 0),
      /** Everyone in scope, for naming an assignee on a row. */
      members,
      /** Valid assignees per app — the roster an assignment must come from. */
      membersByApp,
      conversations,
      loadingConversations,
      reloadConversations: () => loadConversations(scopedAppIds),
      /** Whether older conversations are still on the server, not yet loaded. */
      hasMoreConversations,
      loadingMoreConversations,
      loadMoreConversations,
      patchConversation,
      setConversationMuted,
      toggleConversationPin,
      /**
       * Pinned for the team. Every dashboard on the app answers this the same
       * way, which is the whole difference between a pin and a bookmark.
       */
      isPinned: (conversation) => Boolean(conversation?.pinned),
      setOpenConversation,
      /**
       * Colleagues in a thread, oldest arrival first, excluding you. Empty
       * until somebody actually opens it — silence here means nobody is there.
       */
      viewersOf: (conversationId) =>
        (viewers[conversationId] ?? []).filter((v) => v.userId !== user?.id),
      /**
       * The raw list, oldest arrival first and including you.
       *
       * The order is what decides who may reply without being asked twice, so
       * the one caller that has to answer "am I first?" needs the unfiltered
       * list rather than the courtesy view above.
       */
      conversationViewers: viewers,
      presence,
      onlineVisitors: Object.values(presence.visitors),
      reloadPresence: () => loadPresence(scopedAppIds),
      isVisitorOnline: (visitorId) => Boolean(presence.visitors[visitorId]),
      unreadCount,
      emergencies,
      connection,
      muted,
      toggleMute,
      alertMode,
      setAlertMode,
      isRinging: ringing.active,
      toast,
      dismissToast: () => setToast(null),
      onSocketEvent,
      socket: socketRef,
      refreshApps: async () => setApps(await api.apps.list()),
    }),
    [
      apps,
      appIndex,
      activeApp,
      activeAppId,
      allApps,
      scopedApps,
      scopedAppIds,
      loadingApps,
      selectApp,
      createApp,
      conversations,
      loadingConversations,
      loadConversations,
      hasMoreConversations,
      loadingMoreConversations,
      loadMoreConversations,
      members,
      membersByApp,
      patchConversation,
      setConversationMuted,
      toggleConversationPin,
      setOpenConversation,
      viewers,
      user?.id,
      presence,
      loadPresence,
      appUnread,
      unreadCount,
      emergencies,
      connection,
      muted,
      toggleMute,
      alertMode,
      setAlertMode,
      ringing,
      toast,
      onSocketEvent,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used inside <WorkspaceProvider>');
  return context;
}

export default WorkspaceProvider;
